import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Card, ListGroup, Spinner, Row, Col } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchBidById,
  createBid,
  updateBid,
  deleteBidDocument,
  clearCurrent,
  clearError,
} from '../redux/slices/bidsSlice';
import { fetchCategories } from '../redux/slices/categoriesSlice';

const BidForm: React.FC = () => {
  const navigate  = useNavigate();
  const dispatch  = useAppDispatch();
  const { id }    = useParams<{ id: string }>();
  const isEdit    = !!id;

  const { current: bid, loadingCurrent, submitting, error: bidError } = useAppSelector(s => s.bids);
  const { byContentType, loadingByType } = useAppSelector(s => s.categories);
  const bidCategories = byContentType['bids'] || [];
  const loadingCategories = loadingByType['bids'] ?? false;

  const [formData, setFormData] = useState({
    title:        '',
    description:  '',
    category:     '',
    closing_date: '',
  });
  const [files, setFiles]       = useState<FileList | null>(null);
  const [localError, setLocalError] = useState('');

  // Load categories and bid (if editing) on mount
  useEffect(() => {
    dispatch(fetchCategories({ applies_to: 'bids', active: true }));

    if (isEdit && id) {
      dispatch(fetchBidById(id));
    }

    return () => {
      dispatch(clearCurrent());
      dispatch(clearError());
    };
  }, [dispatch, id, isEdit]);

  // Populate form when bid is loaded
  useEffect(() => {
    if (isEdit && bid) {
      setFormData({
        title:        bid.title,
        description:  bid.description || '',
        category:     bid.category,
        closing_date: bid.closing_date.split('T')[0],
      });
    }
  }, [bid, isEdit]);

  // Set default category once categories load
  useEffect(() => {
    if (!isEdit && bidCategories.length > 0 && !formData.category) {
      setFormData(p => ({ ...p, category: bidCategories[0].slug }));
    }
  }, [bidCategories, isEdit, formData.category]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('category', formData.category);
    data.append('closing_date', formData.closing_date);
    if (files) {
      Array.from(files).forEach(f => data.append('documents', f));
    }

    const result = isEdit
      ? await dispatch(updateBid({ id: id!, formData: data }))
      : await dispatch(createBid(data));

    if (
      createBid.rejected.match(result) ||
      updateBid.rejected.match(result)
    ) {
      setLocalError(result.payload as string || 'Failed to save bid');
      return;
    }

    navigate('/bids');
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Delete this document?')) return;
    const result = await dispatch(deleteBidDocument({ documentId, bidId: id! }));
    if (deleteBidDocument.rejected.match(result)) {
      setLocalError(result.payload as string);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const displayError = localError || bidError;

  if (loadingCurrent) {
    return (
      <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem' }}>
        <Container>
          <div className="text-center" style={{ padding: '60px 0' }}>
            <Spinner animation="border" style={{ color: '#00579a', width: '40px', height: '40px' }} />
            <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading bid...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="mb-4">
          <h1 style={{ color: '#00579a', fontWeight: 600, fontSize: '28px', margin: 0 }}>
            {isEdit ? 'Edit Bid' : 'Create New Bid'}
          </h1>
        </div>

        {displayError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => { setLocalError(''); dispatch(clearError()); }}
            style={{ borderRadius: '6px', border: '1px solid #ffccc7', backgroundColor: '#fff2f0' }}
          >
            {displayError}
          </Alert>
        )}

        <Card style={{ border: 'none', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <Card.Body style={{ padding: '28px' }}>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={8}>
                  {/* Title */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter bid title"
                      required
                      style={inputStyle}
                    />
                  </Form.Group>

                  {/* Description */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Enter bid description"
                      style={{ ...inputStyle, resize: 'none' }}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  {/* Category */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Category *
                      {loadingCategories && (
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ marginLeft: '8px', color: '#8c8c8c', width: '12px', height: '12px' }}
                        />
                      )}
                    </Form.Label>
                    {bidCategories.length === 0 && !loadingCategories ? (
                      <div style={{
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #ffe58f',
                        backgroundColor: '#fffbe6',
                        fontSize: '13px',
                        color: '#874d00',
                      }}>
                        No bid categories found.{' '}
                        <a href="/categories/new" style={{ color: '#00579a' }}>
                          Create one
                        </a>
                      </div>
                    ) : (
                      <Form.Select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                        disabled={loadingCategories}
                        style={inputStyle}
                      >
                        <option value="">Select a category</option>
                        {bidCategories.map(cat => (
                          <option key={cat.id} value={cat.slug}>
                            {cat.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>

                  {/* Closing Date */}
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Closing Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="closing_date"
                      value={formData.closing_date}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Documents */}
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Upload Documents</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={(e: any) => setFiles(e.target.files)}
                  style={inputStyle}
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '12px' }}>
                  You can upload multiple files
                </Form.Text>
              </Form.Group>

              {/* Selected files preview */}
              {files && files.length > 0 && (
                <div className="mb-3" style={{ backgroundColor: '#fafafa', padding: '14px 16px', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                  <strong style={{ fontSize: '13px', color: '#262626', marginBottom: '8px', display: 'block' }}>
                    Selected files:
                  </strong>
                  <ul style={{ fontSize: '13px', marginBottom: 0, paddingLeft: '20px' }}>
                    {Array.from(files).map((f, i) => (
                      <li key={i} style={{ color: '#595959', marginBottom: '2px' }}>
                        {f.name} <span style={{ color: '#8c8c8c' }}>({formatFileSize(f.size)})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Existing documents on edit */}
              {isEdit && bid && bid.documents.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Existing Documents</Form.Label>
                  <ListGroup>
                    {bid.documents.map(doc => (
                      <ListGroup.Item
                        key={doc.id}
                        className="d-flex justify-content-between align-items-center"
                        style={{ border: '1px solid #f0f0f0', borderRadius: '6px', marginBottom: '6px', padding: '10px 14px' }}
                      >
                        <div>
                          <span style={{ color: '#262626', fontSize: '14px', fontWeight: 500 }}>
                            {doc.file_name}
                          </span>
                          {doc.file_size && (
                            <span style={{ marginLeft: '8px', color: '#8c8c8c', fontSize: '12px' }}>
                              ({formatFileSize(doc.file_size)})
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                          style={{
                            backgroundColor: 'white',
                            color: '#ff4d4f',
                            border: '1px solid #ff4d4f',
                            borderRadius: '4px',
                            padding: '3px 10px',
                            fontSize: '12px',
                            fontWeight: 500,
                          }}
                        >
                          Delete
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Form.Group>
              )}

              {/* Form actions */}
              <div className="d-flex gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={submitting || loadingCategories}
                  style={{
                    backgroundColor: '#00579a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
                  }}
                >
                  {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Bid'}
                </Button>
                <Button
                  type="button"
                  onClick={() => navigate('/bids')}
                  disabled={submitting}
                  style={{
                    backgroundColor: 'white',
                    color: '#595959',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontWeight: 500,
  color: '#262626',
  fontSize: '14px',
  marginBottom: '8px',
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px',
};

export default BidForm;